/**
 * Message detail component for rendering full email content
 *
 * @module inbox/MessageDetail
 */

import React from 'react';
import { Flex, Box, Heading, Text, Button, Divider, Dialog, DialogTrigger, DialogFooter } from '@chatsdet/ui';
import { Calendar, Paperclip, Trash } from '@phosphor-icons/react';
import { AddressRow } from './AddressRow';
import { formatDateTime, formatRelative, sanitizeHtml } from './utils';
import type { InboxMessageDetailResponse } from './types';

type MessageDetailProps = {
  message: InboxMessageDetailResponse;
  onDelete?: () => void;
  isDeleting?: boolean;
};

export function MessageDetail({ message, onDelete, isDeleting }: MessageDetailProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const bodyPreference = message.message.htmlBody ? 'html' : 'text';
  const [bodyVariant, setBodyVariant] = React.useState<'text' | 'html'>(bodyPreference);

  React.useEffect(() => {
    setBodyVariant(bodyPreference);
  }, [bodyPreference]);

  const sanitizedHtmlBody = React.useMemo(() => {
    if (!message.message.htmlBody) return null;
    return sanitizeHtml(message.message.htmlBody);
  }, [message.message.htmlBody]);

  const hasTextBody = Boolean(message.message.textBody);
  const hasHtmlBody = Boolean(message.message.htmlBody);

  return (
    <Flex direction="column" gap="lg">
      <Flex align="start" justify="between">
        <Heading level={3} className="text-xl">
          {message.message.subject ?? '(No subject)'}
        </Heading>
        {onDelete && (
          <DialogTrigger size="sm" isOpen={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Delete message"
            >
              <Trash size={18} className="text-muted-foreground" />
            </Button>
            <Dialog title="Delete message">
              <Text size="sm">
                Are you sure you want to permanently delete this message? This action cannot be undone.
              </Text>
              <DialogFooter>
                <Button variant="ghost" onPress={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onPress={() => {
                    setShowDeleteConfirm(false);
                    onDelete();
                  }}
                  isDisabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </DialogFooter>
            </Dialog>
          </DialogTrigger>
        )}
      </Flex>

      <Flex align="center" gap="xs" className="text-muted-foreground text-sm">
        <Calendar size={16} />
        <Text size="sm">
          {formatDateTime(message.message.receivedAt)} ({formatRelative(message.message.receivedAt)})
        </Text>
      </Flex>

      <Divider />

      <Flex direction="column" gap="xs">
        <AddressRow
          label="From"
          addresses={[
            message.message.fromName
              ? `${message.message.fromName} <${message.message.fromAddress}>`
              : message.message.fromAddress,
          ]}
        />
        <AddressRow label="To" addresses={message.message.toAddresses} />
        {message.message.ccAddresses.length > 0 && (
          <AddressRow label="Cc" addresses={message.message.ccAddresses} />
        )}
      </Flex>

      {(hasTextBody || hasHtmlBody) && (
        <Flex direction="column" gap="sm">
          {hasHtmlBody && hasTextBody && (
            <Flex align="center" gap="sm">
              <Button
                variant={bodyVariant === 'html' ? 'secondary' : 'ghost'}
                size="sm"
                onPress={() => setBodyVariant('html')}
              >
                HTML
              </Button>
              <Button
                variant={bodyVariant === 'text' ? 'secondary' : 'ghost'}
                size="sm"
                onPress={() => setBodyVariant('text')}
              >
                Plain text
              </Button>
            </Flex>
          )}

          {bodyVariant === 'html' && hasHtmlBody ? (
            <Box className="border border-border rounded-md p-4 bg-muted/10 prose max-w-none">
              <div dangerouslySetInnerHTML={{ __html: sanitizedHtmlBody ?? '' }} />
            </Box>
          ) : hasTextBody ? (
            <pre className="bg-muted/20 border border-border rounded-md p-4 text-sm whitespace-pre-wrap">
              {message.message.textBody}
            </pre>
          ) : (
            <Text size="sm" className="text-muted-foreground">
              Message body unavailable.
            </Text>
          )}
        </Flex>
      )}

      <Flex direction="column" gap="sm">
        <Heading level={4} className="text-base">
          Attachments
        </Heading>
        {message.message.attachments && message.message.attachments.length > 0 ? (
          <Flex direction="column" gap="xs">
            {message.message.attachments.map((attachment, index) => (
              <Flex
                key={`${attachment.filename ?? 'attachment'}-${index}`}
                align="center"
                justify="between"
                className="border border-border rounded-md px-3 py-2 bg-card/60"
              >
                <Flex align="center" gap="sm">
                  <Paperclip size={16} className="text-muted-foreground" />
                  <Flex direction="column">
                    <Text>{attachment.filename ?? 'Unnamed attachment'}</Text>
                    <Text size="xs" className="text-muted-foreground">
                      {attachment.contentType ?? 'unknown'}
                    </Text>
                  </Flex>
                </Flex>
                {typeof attachment.size === 'number' && (
                  <Text size="sm" className="text-muted-foreground">
                    {Math.round(attachment.size / 1024)} KB
                  </Text>
                )}
              </Flex>
            ))}
          </Flex>
        ) : (
          <Text size="sm" className="text-muted-foreground">
            No attachments.
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
